import { useCallback, useEffect, useState } from 'react';
import { ParamSchema } from '../api/parameter';
import { listMMGroups, MMGroup } from '../api/mmode';
import { listDict, DictEntry } from '../api/dict';

// 参数模板键 (设备 · 加工模式 · 加工类型) 的展示名.
// 加工模式/加工类型的中文一律从字典取 (mm_group / dict process_type), 页面不抄常量表;
// 模板管理页与参数批量导入弹窗共用这一份, 避免两处各写一套 label 拼装.
export function useSchemaKeyLabel(arch: string) {
    const [groups, setGroups] = useState<MMGroup[]>([]);
    const [subProcs, setSubProcs] = useState<DictEntry[]>([]);

    useEffect(() => {
        listMMGroups()
            .then(setGroups)
            .catch(() => setGroups([]));
    }, []);

    useEffect(() => {
        if (!arch) {
            setSubProcs([]);
            return;
        }
        listDict('process_type', { scope: arch })
            .then(setSubProcs)
            .catch(() => setSubProcs([]));
    }, [arch]);

    const groupName = useCallback(
        (code: string) => groups.find((g) => g.code === code)?.nameZh || code,
        [groups],
    );
    const subProcLabel = useCallback(
        (code: string) => subProcs.find((s) => s.code === code)?.label || code,
        [subProcs],
    );

    const keyLabel = useCallback(
        // 键段可缺省 (参数行详情的命中键是可选字段); 缺省与空串同义 = 该维度通配.
        // 模板按设备建 (2026-11-01); 没有设备段的是存量未拆分模板, 显示其机型.
        (
            t: Partial<Pick<ParamSchema, 'deviceKey' | 'family' | 'groupCode' | 'processType'>>,
        ): string => {
            if (!t.deviceKey && !t.family && !t.groupCode && !t.processType) return '框架默认 (兜底)';
            return [
                t.deviceKey || (t.family ? `机型 ${t.family} (待拆分)` : '任意设备'),
                t.groupCode ? groupName(t.groupCode) : '任意加工模式',
                t.processType ? subProcLabel(t.processType) : '任意加工类型',
            ].join(' · ');
        },
        [groupName, subProcLabel],
    );

    return { groups, subProcs, groupName, subProcLabel, keyLabel };
}
